import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { t as translate } from "../i18n/translations.js";
import ProblemReportLink from '../components/ProblemReportLink.jsx';

const API_BASE = "/api";

const DEFAULTS = {
  primaryColor: "#6366f1", secondaryColor: "#8b5cf6", accentColor: "#10b981",
  backgroundColor: "#f8f7ff", textColor: "#1a1a1a",
  fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  borderRadius: "20", buttonStyle: "gradient",
};

function getToken() { return localStorage.getItem("zyncfit_customer_token"); }
function getCustomer() { try { return JSON.parse(localStorage.getItem("zyncfit_customer") || "null"); } catch { return null; } }
function setAuthStorage(token, customer) { localStorage.setItem("zyncfit_customer_token", token); localStorage.setItem("zyncfit_customer", JSON.stringify(customer)); }
function clearAuth() { localStorage.removeItem("zyncfit_customer_token"); localStorage.removeItem("zyncfit_customer"); }

async function apiFetch(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...options.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Er ging iets mis");
  return data;
}

function b(branding) { return { ...DEFAULTS, ...branding }; }

function btnStyle(br, override) {
  const r = parseInt(br.borderRadius || "20");
  const base = {
    display: "block", width: "100%", padding: "16px 24px", fontSize: 16,
    fontWeight: 700, borderRadius: r, cursor: "pointer", textAlign: "center",
    textDecoration: "none", boxSizing: "border-box", fontFamily: br.fontFamily,
    WebkitTapHighlightColor: "transparent", transition: "all 0.2s ease", border: "none",
  };
  if (br.buttonStyle === "outline")
    return { ...base, background: "transparent", color: br.primaryColor, border: `2px solid ${br.primaryColor}`, ...override };
  if (br.buttonStyle === "solid")
    return { ...base, background: br.primaryColor, color: "#fff", ...override };
  return { ...base, background: `linear-gradient(135deg, ${br.primaryColor}, ${br.secondaryColor})`, color: "#fff", ...override };
}

/* ─── Auth Form ─── */
function AuthForm({ onAuth, br, lang }) {
  const radius = parseInt(br.borderRadius);
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const inputStyle = {
    width: "100%", padding: "15px 16px", fontSize: 15, borderRadius: radius * 0.5,
    border: `1.5px solid ${br.primaryColor}20`, outline: "none", boxSizing: "border-box",
    marginBottom: 12, fontFamily: br.fontFamily, transition: "border-color 0.2s, box-shadow 0.2s",
    background: "#fff",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (mode === "register" && !privacyAccepted) {
      setError(translate(lang, "dashboard.privacyError"));
      return;
    }
    setLoading(true);
    try {
      const endpoint = mode === "register" ? "/customer/register" : "/customer/login";
      const body = mode === "register" ? { email, password, name, privacyAccepted, marketingConsent } : { email, password };
      const data = await apiFetch(endpoint, { method: "POST", body: JSON.stringify(body) });
      setAuthStorage(data.token, data.customer);
      onAuth(data.customer);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: "100dvh", background: br.backgroundColor, fontFamily: br.fontFamily, color: br.textColor, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <style>{CSS_ANIMATIONS}</style>

      <div style={{ width: "100%", maxWidth: 420, animation: "fadeUp 0.5s ease both" }}>
        {/* Brand header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          {br.logoUrl && (
            <img src={br.logoUrl} alt="" style={{ maxHeight: 56, marginBottom: 12 }} />
          )}
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>
            {br.storeName || "ZyncFit"}
          </h1>
          <p style={{ fontSize: 14, color: `${br.textColor}70`, margin: "6px 0 0" }}>{translate(lang, "dashboard.virtualFittingRoom")}</p>
        </div>

        {/* Auth card */}
        <div style={{ background: "#fff", borderRadius: radius, padding: 28, boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 24px rgba(0,0,0,0.08)" }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 20px" }}>
            {mode === "login" ? translate(lang, "dashboard.welcomeBack") : translate(lang, "dashboard.createAccount")}
          </h2>

          <form onSubmit={handleSubmit}>
            {mode === "register" && (
              <input style={inputStyle} type="text" placeholder={translate(lang, "dashboard.namePlaceholder")} value={name}
                onChange={(e) => setName(e.target.value)} autoComplete="name"
                onFocus={(e) => { e.target.style.borderColor = br.primaryColor; e.target.style.boxShadow = `0 0 0 3px ${br.primaryColor}15`; }}
                onBlur={(e) => { e.target.style.borderColor = `${br.primaryColor}20`; e.target.style.boxShadow = "none"; }} />
            )}
            <input style={inputStyle} type="email" placeholder={translate(lang, "common.email")} value={email}
              onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
              onFocus={(e) => { e.target.style.borderColor = br.primaryColor; e.target.style.boxShadow = `0 0 0 3px ${br.primaryColor}15`; }}
              onBlur={(e) => { e.target.style.borderColor = `${br.primaryColor}20`; e.target.style.boxShadow = "none"; }} />
            <input style={inputStyle} type="password" placeholder={translate(lang, "common.password")} value={password}
              onChange={(e) => setPassword(e.target.value)} required minLength={6}
              autoComplete={mode === "register" ? "new-password" : "current-password"}
              onFocus={(e) => { e.target.style.borderColor = br.primaryColor; e.target.style.boxShadow = `0 0 0 3px ${br.primaryColor}15`; }}
              onBlur={(e) => { e.target.style.borderColor = `${br.primaryColor}20`; e.target.style.boxShadow = "none"; }} />

            {mode === "register" && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: `${br.textColor}90`, cursor: "pointer", marginBottom: 10, lineHeight: 1.5 }}>
                  <input type="checkbox" checked={privacyAccepted} onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    style={{ marginTop: 3, accentColor: br.primaryColor, width: 16, height: 16 }} />
                  <span><span dangerouslySetInnerHTML={{ __html: translate(lang, "dashboard.privacyLabel") }} /> <span style={{ color: "#e53e3e" }}>{translate(lang, "dashboard.privacyRequired")}</span></span>
                </label>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: `${br.textColor}90`, cursor: "pointer", lineHeight: 1.5 }}>
                  <input type="checkbox" checked={marketingConsent} onChange={(e) => setMarketingConsent(e.target.checked)}
                    style={{ marginTop: 3, accentColor: br.primaryColor, width: 16, height: 16 }} />
                  <span>{translate(lang, "dashboard.marketingLabel")}</span>
                </label>
              </div>
            )}

            {error && (
              <div style={{ background: "#fef2f2", color: "#dc2626", padding: "12px 16px", borderRadius: radius * 0.4, fontSize: 13, marginBottom: 12, fontWeight: 500 }}>
                {error}
              </div>
            )}

            <button style={{ ...btnStyle(br), opacity: loading ? 0.6 : 1 }} type="submit" disabled={loading}>
              {loading ? translate(lang, "dashboard.pleaseWait") : mode === "login" ? translate(lang, "dashboard.login") : translate(lang, "dashboard.createAccount")}
            </button>
          </form>

          <div style={{ textAlign: "center", marginTop: 16 }}>
            <button
              style={{ background: "none", border: "none", color: br.primaryColor, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: br.fontFamily }}
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(null); }}
            >
              {mode === "login" ? translate(lang, "dashboard.noAccount") : translate(lang, "dashboard.hasAccount")}
            </button>
          </div>
        </div>

        <div style={{ textAlign: "center", padding: "24px 0", color: `${br.textColor}35`, fontSize: 12 }}>{translate(lang, "common.poweredBy")}</div>
      </div>
    </div>
  );
}

/* ─── Dashboard ─── */
function Dashboard({ customer, onLogout, br, onBrandingLoaded, lang, onLanguageLoaded }) {
  const navigate = useNavigate();
  const radius = parseInt(br.borderRadius);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSessions = useCallback(async () => {
    try {
      const data = await apiFetch("/customer/sessions");
      const list = data.sessions || [];
      setSessions(list);
      // Fetch branding from the first session's shop
      if (list.length > 0 && list[0].shopDomain) {
        fetch(`${API_BASE}/branding/public/${encodeURIComponent(list[0].shopDomain)}`)
          .then((r) => r.json()).then(onBrandingLoaded).catch(() => {});
        fetch(`${API_BASE}/settings/public/${encodeURIComponent(list[0].shopDomain)}`)
          .then((r) => r.json()).then((s) => { if (s.language) onLanguageLoaded(s.language); }).catch(() => {});
      }
    }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [onBrandingLoaded, onLanguageLoaded]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const initials = (customer.name || customer.email || "?").split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 2);

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" }); }
    catch { return ""; }
  };

  return (
    <div style={{ minHeight: "100dvh", background: br.backgroundColor, fontFamily: br.fontFamily, color: br.textColor }}>
      <style>{CSS_ANIMATIONS}</style>

      <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 16px 40px" }}>
        {/* Top bar */}
        <div style={{ display: "flex", flexWrap: 'wrap', gap: 12, justifyContent: "space-between", alignItems: "center", padding: "16px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 42, height: 42, borderRadius: "50%",
              background: `linear-gradient(135deg, ${br.primaryColor}, ${br.secondaryColor})`,
              color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 15, fontWeight: 700, boxShadow: `0 2px 8px ${br.primaryColor}30`,
            }}>
              {initials}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{customer.name || customer.email}</div>
              {customer.name && <div style={{ fontSize: 12, color: `${br.textColor}60` }}>{customer.email}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
            <ProblemReportLink style={{ color: br.primaryColor }} />
            <button onClick={onLogout} style={{ fontSize: 13, color: `${br.textColor}50`, background: "none", border: "none", cursor: "pointer", padding: 8, fontFamily: br.fontFamily, fontWeight: 500 }}>
              {translate(lang, "dashboard.logout")}
            </button>
          </div>
        </div>

        {/* Header */}
        <div style={{ textAlign: "center", padding: "16px 0 24px", animation: "fadeUp 0.4s ease both" }}>
          {br.logoUrl && <img src={br.logoUrl} alt="" style={{ maxHeight: 40, marginBottom: 8 }} />}
          <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, letterSpacing: -0.3 }}>{translate(lang, "dashboard.myFittingRoom")}</h1>
          <p style={{ fontSize: 14, color: `${br.textColor}60`, margin: "6px 0 0" }}>
            {sessions.length > 0 ? translate(lang, "dashboard.outfitsGenerated", { count: sessions.length }) : translate(lang, "dashboard.viewOutfits")}
          </p>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", border: `3px solid ${br.primaryColor}`, borderTopColor: "transparent", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
            <p style={{ color: `${br.textColor}60`, fontSize: 14 }}>{translate(lang, "common.loading")}</p>
          </div>
        ) : error ? (
          <div style={{ background: "#fff", borderRadius: radius, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 20px rgba(0,0,0,0.06)" }}>
            <div style={{ background: "#fef2f2", color: "#dc2626", padding: "12px 16px", borderRadius: radius * 0.4, fontSize: 13, marginBottom: 12 }}>{error}</div>
            <button onClick={loadSessions} style={{ ...btnStyle(br), background: `${br.primaryColor}10`, color: br.primaryColor, fontSize: 14, padding: "12px" }}>
              {translate(lang, "common.retry")}
            </button>
          </div>
        ) : sessions.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", animation: "fadeUp 0.5s ease both" }}>
            <h3 style={{ margin: "0 0 8px", fontWeight: 700, fontSize: 18 }}>{translate(lang, "dashboard.noOutfits")}</h3>
            <p style={{ fontSize: 14, color: `${br.textColor}60`, lineHeight: 1.6 }}>
              {translate(lang, "dashboard.noOutfitsDesc")}
            </p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {sessions.map((sess, i) => (
              <div
                key={sess.id}
                onClick={() => navigate(`/my-outfits/${sess.id}`)}
                style={{
                  background: "#fff", borderRadius: radius, overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06)",
                  cursor: "pointer", transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  animation: "fadeUp 0.5s ease both", animationDelay: `${i * 0.06}s`,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = `0 8px 30px ${br.primaryColor}15`; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.06)"; }}
              >
                {/* Image */}
                {sess.resultImageB64 ? (
                  <img
                    src={`data:${sess.resultImageB64.startsWith("/9j/") ? "image/jpeg" : "image/png"};base64,${sess.resultImageB64}`}
                    alt={sess.product?.title || "AI Outfit"}
                    style={{ width: "100%", height: 220, objectFit: "cover", display: "block" }}
                  />
                ) : sess.product?.garmentImageUrl ? (
                  <img
                    src={sess.product.garmentImageUrl}
                    alt={sess.product?.title || ""}
                    style={{ width: "100%", height: 220, objectFit: "cover", display: "block" }}
                  />
                ) : (
                  <div style={{ width: "100%", height: 220, background: `linear-gradient(135deg, ${br.primaryColor}08, ${br.secondaryColor}08)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: `${br.textColor}30`, textTransform: "uppercase", letterSpacing: 1 }}>{translate(lang, "dashboard.noImage")}</span>
                  </div>
                )}

                {/* Info */}
                <div style={{ padding: "14px 16px" }}>
                  <p style={{ fontSize: 14, fontWeight: 700, margin: 0, lineHeight: 1.3 }}>
                    {sess.product?.title || translate(lang, "dashboard.unknownProduct")}
                  </p>
                  <p style={{ fontSize: 12, color: `${br.textColor}50`, marginTop: 4, marginBottom: 0 }}>
                    {formatDate(sess.createdAt)}
                  </p>
                  {sess.hasResultImage && (
                    <span style={{
                      display: "inline-block", fontSize: 10, fontWeight: 700, padding: "3px 8px",
                      borderRadius: 6, marginTop: 8, background: `${br.primaryColor}12`, color: br.primaryColor,
                      textTransform: "uppercase", letterSpacing: 0.5,
                    }}>
                      {translate(lang, "dashboard.aiGenerated")}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ textAlign: "center", padding: "32px 0 16px", color: `${br.textColor}35`, fontSize: 12 }}>{translate(lang, "common.poweredBy")}</div>
      </div>
    </div>
  );
}

/* ─── Main Component ─── */
export default function CustomerDashboardPage() {
  const [customer, setCustomer] = useState(getCustomer);
  const [checked, setChecked] = useState(false);
  const [branding, setBranding] = useState(null);
  const [lang, setLang] = useState("nl");
  const br = b(branding || {});

  useEffect(() => {
    const token = getToken();
    if (token && customer) {
      apiFetch("/customer/me")
        .then((data) => { setCustomer(data.customer); setAuthStorage(token, data.customer); })
        .catch(() => { clearAuth(); setCustomer(null); })
        .finally(() => setChecked(true));
    } else {
      setChecked(true);
    }
    // Branding will be fetched after sessions load (need shopDomain)
  }, []);

  if (!checked) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100dvh", background: br.backgroundColor }}>
        <style>{CSS_ANIMATIONS}</style>
        <div style={{ width: 48, height: 48, borderRadius: "50%", border: `3px solid ${br.primaryColor}`, borderTopColor: "transparent", animation: "spin 0.8s linear infinite" }} />
      </div>
    );
  }

  if (!customer) return <AuthForm onAuth={setCustomer} br={br} lang={lang} />;

  return <Dashboard customer={customer} onLogout={() => { clearAuth(); setCustomer(null); }} br={br} onBrandingLoaded={setBranding} lang={lang} onLanguageLoaded={setLang} />;
}

const CSS_ANIMATIONS = `
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Playfair+Display:wght@700&family=Poppins:wght@400;600;700&family=Montserrat:wght@400;600;700&display=swap');
`;
